/* eslint-disable react/jsx-indent */
import { useUser } from '@folklore/auth';
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';

import { useThemeClassName, useTheme } from '../../contexts/ThemeContext';
import ProfileButton from '../buttons/ProfileButton';
import StyledButton from '../buttons/StyledButton';
import Leader from '../partials/Leader';
import Leaderboard from '../partials/Leaderboard';
import PageTitle from '../partials/PageTitle';
import UserLetter from '../partials/UserLetter';
import UserRank from '../partials/UserRank';

import styles from '../../../styles/pages/leaderboard.module.scss';

const propTypes = {
    page: AppPropTypes.page.isRequired,
};

function LeaderboardPage({ page }) {
    const route = useUrlGenerator();
    const { name: theme } = useTheme();
    const themeClassName = useThemeClassName(styles);
    const user = useUser();
    const {
        title = 'Page',
        description = null,
        leaders = null,
        count = null,
        currentParticipant = null,
        tables = null,
        withoutTable = true,
    } = page || {};

    const { stats = {} } = user || {};

    // TODO: leaderboardPosition in stats is always empty ?
    const { score = 0, leaderboardPosition: userLeaderboardPosition = null } = stats || {};

    const isTradition = theme === 'tradition';
    const isPerfect = theme === 'perfect';

    return (
        <div className={classNames([styles.container, themeClassName])}>
            <div className={styles.content}>
                {isPerfect ? (
                    <div className={styles.header}>
                        <UserLetter className={styles.user} user={user} withName />
                        <div className={styles.totalPoints}>
                            <div className={styles.points}>
                                {score || 0} <span>pts</span>
                            </div>
                        </div>
                    </div>
                ) : null}

                <PageTitle title={title} className={styles.title} />
                {description !== null ? <p className={styles.description}>{description}</p> : null}

                {/* @TODO: Move to UserRank once currentParticipant works */}
                {!isPerfect ? (
                    <div className={styles.profileLinkContainer}>
                        <ProfileButton user={user} />
                    </div>
                ) : null}

                {isTradition && userLeaderboardPosition !== null ? (
                    <UserRank user={user} participant={currentParticipant} />
                ) : null}

                <div className={styles.tables}>
                    {tables !== null
                        ? (tables || []).map(({ title: tableTitle = null, items = [] }) =>
                              items !== null ? (
                                  <div className={styles.table} key={`table-${tableTitle}`}>
                                      {tableTitle !== null ? (
                                          <h3 className={styles.tableTitle}>{tableTitle}</h3>
                                      ) : null}
                                      <Leaderboard
                                          columns={null}
                                          items={items || []}
                                          className={styles.leaderboard}
                                      />
                                  </div>
                              ) : null,
                          )
                        : null}
                </div>

                {!withoutTable && leaders !== null && leaders.length > 0 ? (
                    <Leaderboard items={leaders} count={count} className={styles.leaderboard} />
                ) : null}

                {withoutTable ? (
                    <div className={styles.leaders}>
                        {(leaders || []).map((leader, index) => (
                            <Leader
                                key={`leader-${index + 1}`}
                                className={styles.leader}
                                position={String(index + 1).padStart(2, '0')}
                                leader={leader}
                            />
                        ))}
                    </div>
                ) : null}

                {leaders === null || leaders.length === 0 ? (
                    <p className={styles.empty}>
                        <FormattedMessage
                            defaultMessage="No good answer yet!"
                            description="Display message"
                        />
                    </p>
                ) : null}

                {(leaders === null || leaders.length === 0) && tables === null ? (
                    <div className={styles.noLeaders}>
                        <FormattedMessage
                            defaultMessage="No participants yet."
                            description="Leaderboard page"
                        />
                    </div>
                ) : null}

                <StyledButton className={styles.back} href={route('play')}>
                    <FormattedMessage defaultMessage="Back to games" description="Play button" />
                </StyledButton>
            </div>
        </div>
    );
}

LeaderboardPage.propTypes = propTypes;

export default LeaderboardPage;
